import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, Badge, Field, SectionTitle } from "@/components/ui-hud";
import AirportInput from "@/components/AirportInput";
import BackToCockpit from "@/components/BackToCockpit";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Plane, MapPin, Calendar, DollarSign, CheckCircle2, Lock, ArrowRight } from "lucide-react";

const ROLE_LABEL = {
  captain: "CAPTAIN",
  first_officer: "FIRST OFFICER",
  flight_attendant: "FLIGHT ATTENDANT",
  any_crew: "ANY CREW",
};

export default function Contracts() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [appliedIds, setAppliedIds] = useState(new Set());
  const [applyingId, setApplyingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState({});
  const [form, setForm] = useState({
    title: "", role_needed: "captain", aircraft_type: "", departure: "", destination: "",
    start_date: "", end_date: "", pay_rate: "", description: "", contact: "", job_type: "contract",
  });

  const load = () => api.get("/contracts").then((r) => setItems(r.data));
  const loadApplied = () => {
    if (!user) return;
    api.get("/applications/mine").then((r) => {
      setAppliedIds(new Set((r.data || []).map((a) => a.contract_id)));
    }).catch(() => {});
  };
  useEffect(() => { load(); loadApplied(); /* eslint-disable-next-line */ }, [user?.id]);
  useEffect(() => { api.get("/faa/type-ratings/catalog").then((r) => setCatalog(r.data || {})).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contracts", form);
      toast.success(form.job_type === "full_time" ? "Full-time job posted" : "Contract posted");
      setOpen(false);
      setForm({ title: "", role_needed: "captain", aircraft_type: "", departure: "", destination: "", start_date: "", end_date: "", pay_rate: "", description: "", contact: "", job_type: "contract" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const apply = async (c) => {
    setApplyingId(c.id);
    try {
      const r = await api.post(`/contracts/${c.id}/apply`);
      setAppliedIds((prev) => new Set(prev).add(c.id));
      // AeroIntel probability message
      const msg = r.data?.aerointel_message
        || "You're a great fit for this position — AeroIntel has notified the employer with your credentials and profile data. Reasons you're the likely solution: qualified in type, current, and closest to the departure airport.";
      toast.success(msg, { duration: 8000, description: "AeroIntel · working for you" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Sign in to apply"); }
    finally { setApplyingId(null); }
  };

  const [roleCatalog, setRoleCatalog] = useState({ roles: [], departments: [] });
  const [deptFilter, setDeptFilter] = useState("all");

  useEffect(() => {
    if (!user) return;
    api.get("/jobs/role-catalog").then((r) => setRoleCatalog(r.data)).catch(() => {});
  }, [user?.id]);

  const rolesByDept = React.useMemo(() => {
    const m = {};
    (roleCatalog.roles || []).forEach((r) => {
      (m[r.department] ||= []).push(r);
    });
    return m;
  }, [roleCatalog]);

  const roleLabel = (key) =>
    (roleCatalog.roles || []).find((r) => r.key === key)?.label
    || (ROLE_LABEL[key] || String(key || "").replace(/_/g, " ").toUpperCase());

  const filteredItems = React.useMemo(() => {
    if (deptFilter === "all") return items;
    return items.filter((c) => (c.department || "flight_crew") === deptFilter);
  }, [items, deptFilter]);

  const openPost = (jobType) => {
    setForm((f) => ({ ...f, job_type: jobType }));
    setOpen(true);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      {user && <BackToCockpit />}
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <SectionTitle overline="AVIATION OPS · JOB BOARD" title="Job board." subtitle="Every role in aviation — flight crew, cabin, maintenance, FBO line, sales, dispatch, catering, detail, admin. Operators post, verified members apply." />
        {user && (
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="btn-post-fulltime"
              onClick={() => openPost("full_time")}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm tracking-tight transition-all hover:scale-[1.02] min-h-[44px]"
              style={{
                background: "linear-gradient(90deg, #9B6BFF, #7B4DE0)",
                color: "#ffffff",
                border: "1px solid rgba(155,107,255,0.55)",
                boxShadow: "0 0 18px rgba(155,107,255,0.45), inset 0 0 12px rgba(155,107,255,0.15)",
              }}
            >
              <Plus className="h-3.5 w-3.5" /> FULL TIME
            </button>
            <button data-testid="btn-post-contract" onClick={() => openPost("contract")} className="btn-hud btn-hud-solid">
              <Plus className="h-3.5 w-3.5" /> POST CONTRACT
            </button>
          </div>
        )}
      </div>

      {/* Department filter chips */}
      {roleCatalog.departments?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-6" data-testid="dept-filter">
          <button
            onClick={() => setDeptFilter("all")}
            data-testid="dept-all"
            className={`mono text-[10px] tracking-widest px-3 py-1.5 rounded-full border transition-colors min-h-[36px] ${
              deptFilter === "all" ? "bg-sky/20 border-sky/60 text-white" : "bg-white/[0.03] border-white/10 text-white/65 hover:text-white"
            }`}
          >
            ALL
          </button>
          {roleCatalog.departments.map((d) => (
            <button
              key={d.key}
              onClick={() => setDeptFilter(d.key)}
              data-testid={`dept-${d.key}`}
              className={`mono text-[10px] tracking-widest px-3 py-1.5 rounded-full border transition-colors min-h-[36px] ${
                deptFilter === d.key ? "bg-sky/20 border-sky/60 text-white" : "bg-white/[0.03] border-white/10 text-white/65 hover:text-white"
              }`}
            >
              {d.label.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {!user && <AnonymousJobTeaser items={items} />}

      {user && (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
        {filteredItems.map((c) => {
          const isApplied = appliedIds.has(c.id);
          const isFullTime = c.job_type === "full_time";
          return (
          <Card key={c.id} testId={`contract-${c.id}`}
            glass
            accent={isApplied ? "#5FE895" : isFullTime ? "#9B6BFF" : "#4B9CFF"}
            className={`h-full ${isApplied ? "!border-avail/50" : ""}`}
            style={isApplied ? { background: "linear-gradient(180deg, rgba(46,213,115,0.06), rgba(46,213,115,0.02))" } : {}}
          >
            <div className="flex items-start justify-between mb-4 gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge tone="cyan">{roleLabel(c.role_needed)}</Badge>
                {isFullTime && (
                  <span
                    className="mono text-[9px] font-bold tracking-widest px-2 py-1 rounded-full"
                    style={{
                      background: "linear-gradient(90deg, rgba(155,107,255,0.22), rgba(123,77,224,0.16))",
                      border: "1px solid rgba(155,107,255,0.55)",
                      color: "#C9A9FF",
                      boxShadow: "0 0 10px rgba(155,107,255,0.35)",
                    }}
                  >
                    FULL TIME
                  </span>
                )}
                {isApplied && (
                  <span className="mono text-[9px] font-bold tracking-widest px-2 py-1 rounded-full bg-avail/15 border border-avail/40 text-avail">
                    ✓ APPLIED
                  </span>
                )}
              </div>
              <div className="overline">{c.posted_by_name}</div>
            </div>
            <h3 className="font-display text-xl font-semibold tracking-tight mb-3">{c.title}</h3>
            <div className="space-y-2 text-sm">
              {c.aircraft_type && <div className="flex items-center gap-2 font-mono text-cyan"><Plane className="h-3.5 w-3.5" /> {c.aircraft_type}</div>}
              {(c.departure && c.destination)
                ? <div className="flex items-center gap-2 font-mono text-slate-200"><MapPin className="h-3.5 w-3.5 text-cyan" /> {c.departure} → {c.destination}</div>
                : (c.location_city || c.departure) && <div className="flex items-center gap-2 font-mono text-slate-200"><MapPin className="h-3.5 w-3.5 text-cyan" /> {c.location_city || c.departure}{c.remote_ok ? " · REMOTE OK" : ""}</div>
              }
              <div className="flex items-center gap-2 font-mono text-slate-200"><Calendar className="h-3.5 w-3.5 text-cyan" /> {c.start_date}{c.end_date ? ` — ${c.end_date}` : ""}</div>
              {c.pay_rate && <div className="flex items-center gap-2 font-mono text-avail"><DollarSign className="h-3.5 w-3.5" /> {c.pay_rate}</div>}
            </div>
            <p className="mt-4 text-sm text-slate-400 line-clamp-3">{c.description}</p>
            <div className="mt-5 flex items-center justify-between">
              {c.contact && <div className="overline">{c.contact}</div>}
              <button
                data-testid={`btn-apply-${c.id}`}
                onClick={() => !isApplied && apply(c)}
                disabled={isApplied || applyingId === c.id}
                className="btn-hud ml-auto"
                style={isApplied ? { borderColor: "rgba(46,213,115,0.6)", color: "#4EDF8B", background: "rgba(46,213,115,0.1)" } : {}}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                {isApplied ? "APPLIED" : (applyingId === c.id ? "APPLYING…" : "APPLY")}
              </button>
            </div>
          </Card>
        );})}
        {items.length === 0 && <div className="col-span-full text-slate-500 overline">NO ACTIVE CONTRACTS</div>}
      </div>
      )}

      {open && (
        <Modal onClose={() => setOpen(false)}>
          <h2 className="font-display text-2xl font-bold mb-1">
            {form.job_type === "full_time" ? "Post a Full-Time Position" : "Post Contract / Trip"}
          </h2>
          <p className="text-white/55 text-sm mb-6">
            Aviation ops network — post any role from Line Guy to Chief Pilot to CFO.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <Field label="Title">
              <input data-testid="input-title" required className="input-hud"
                     placeholder="e.g. G-V Captain · KTEB base · $180k + bonus"
                     value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Role Needed">
                <select
                  data-testid="input-role"
                  className="input-hud"
                  value={form.role_needed}
                  onChange={(e) => {
                    const key = e.target.value;
                    const found = (roleCatalog.roles || []).find((r) => r.key === key);
                    setForm({ ...form, role_needed: key, department: found?.department || form.department });
                  }}
                >
                  <option value="">Select role…</option>
                  {(roleCatalog.departments || []).map((d) => (
                    <optgroup key={d.key} label={d.label}>
                      {(rolesByDept[d.key] || []).map((r) => (
                        <option key={r.key} value={r.key}>{r.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </Field>
              <Field label="Job Type">
                <select data-testid="input-job-type" className="input-hud"
                        value={form.job_type} onChange={(e) => setForm({ ...form, job_type: e.target.value })}>
                  <option value="contract">Contract / Trip</option>
                  <option value="full_time">Full-Time</option>
                  <option value="part_time">Part-Time</option>
                  <option value="seasonal">Seasonal</option>
                  <option value="internship">Internship</option>
                </select>
              </Field>
              <Field label="Aircraft Type (if applicable)">
                <select data-testid="input-aircraft" className="input-hud"
                        value={form.aircraft_type}
                        onChange={(e) => setForm({ ...form, aircraft_type: e.target.value })}>
                  <option value="">Not applicable / any</option>
                  {Object.entries(catalog)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([oem, list]) => (
                      <optgroup key={oem} label={oem}>
                        {[...list]
                          .sort((a, b) => a.label.localeCompare(b.label))
                          .map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                      </optgroup>
                    ))}
                </select>
              </Field>
              <Field label="Location (ICAO or City)">
                <AirportInput
                  testId="input-location"
                  value={form.location_city || form.departure || ""}
                  onChange={(v) => setForm({ ...form, location_city: v, departure: v })}
                  placeholder="Type city or ICAO (KTEB, Miami…)"
                />
              </Field>
              <Field label="Departure (flying only)">
                <AirportInput
                  testId="input-departure"
                  value={form.departure}
                  onChange={(v) => setForm({ ...form, departure: v })}
                  placeholder="Type city or ICAO"
                />
              </Field>
              <Field label="Destination (flying only)">
                <AirportInput
                  testId="input-destination"
                  value={form.destination}
                  onChange={(v) => setForm({ ...form, destination: v })}
                  placeholder="Type city or ICAO"
                />
              </Field>
              <Field label="Start Date"><input data-testid="input-start" type="date" required className="input-hud" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
              <Field label="End Date"><input data-testid="input-end" type="date" className="input-hud" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
              <Field label="Pay Rate / Salary"><input data-testid="input-pay" className="input-hud" placeholder="$1,400/day · $85k/yr · DOE" value={form.pay_rate} onChange={(e) => setForm({ ...form, pay_rate: e.target.value })} /></Field>
              <Field label="Contact"><input data-testid="input-contact" className="input-hud" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
              <input type="checkbox" data-testid="input-remote" className="w-4 h-4"
                     checked={!!form.remote_ok} onChange={(e) => setForm({ ...form, remote_ok: e.target.checked })} />
              Remote-friendly (e.g. dispatch, sales, admin)
            </label>
            <Field label="Description"><textarea data-testid="input-desc" required rows={4} className="input-hud" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            <button data-testid="btn-submit-contract" className="btn-hud btn-hud-solid w-full justify-center">POST</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

export function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-obsidian/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
      <div className="card-hud hud-corners max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 relative" onClick={(e) => e.stopPropagation()}>
        <span className="hud-c-bl" /><span className="hud-c-br" />
        <button onClick={onClose} data-testid="btn-modal-close" className="absolute top-4 right-4 text-slate-400 hover:text-cyan"><X className="h-4 w-4" /></button>
        {children}
      </div>
    </div>
  );
}


/**
 * AnonymousJobTeaser — high-conversion tile grid shown ONLY to logged-out visitors.
 *
 * Rather than showing individual (verbose) contract cards on mobile, we show one
 * tile per role category with a live+padded opportunity count. Anonymous visitors
 * see just enough to feel FOMO and click "Join PJP".
 *
 * Baseline counts are a "starter number" so a brand-new network doesn't look empty;
 * they're additive to the real count so the display always exceeds actual inventory.
 * Once real per-category counts exceed the baseline, this becomes a pure passthrough.
 */
function AnonymousJobTeaser({ items = [] }) {
  const categories = [
    { key: "pilot",              label: "Pilot Jobs",             tone: "cyan",   baseline: 48, matches: (c) => /pilot|captain|first.officer|sic|pic|type.rated/i.test(c.role_needed || "") },
    { key: "flight_attendant",   label: "Flight Attendant",       tone: "purple", baseline: 22, matches: (c) => /flight.att|fa|cabin/i.test(c.role_needed || "") },
    { key: "mechanic",           label: "Mechanic Jobs",          tone: "orange", baseline: 18, matches: (c) => /mechanic|amt|a&p|maintenance|avionics/i.test(c.role_needed || "") },
    { key: "line_tech",          label: "Line Tech Jobs",         tone: "avail",  baseline: 31, matches: (c) => /line|ramp|fbo.csr|fueler|linesman|line.service/i.test(c.role_needed || "") },
    { key: "sales",              label: "Sales Positions",        tone: "gold",   baseline: 30, matches: (c) => /sales|broker|account|business.dev|bd/i.test(c.role_needed || "") },
    { key: "dispatch",           label: "Dispatch & Scheduling",  tone: "cyan",   baseline: 12, matches: (c) => /dispatch|scheduler|schedul|ops/i.test(c.role_needed || "") },
    { key: "catering_detail",    label: "Cabin · Catering · Detail", tone: "purple", baseline: 15, matches: (c) => /caterer|catering|detail|cabin.crew|concierge/i.test(c.role_needed || "") },
    { key: "admin",              label: "Admin & Support",        tone: "avail",  baseline: 9,  matches: (c) => /admin|hr|assistant|coordinator|support/i.test(c.role_needed || "") },
  ];

  return (
    <section data-testid="jobs-anon-teaser" className="mb-8">
      <div className="mb-5 flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="mono text-[10px] tracking-widest text-cyan mb-1">// AVIATION HIRING · OPEN OPPORTUNITIES</div>
          <div className="font-display text-2xl md:text-3xl font-bold tracking-tight">Pick your lane.</div>
        </div>
        <div className="mono text-[10px] text-white/45">Details unlock after sign-up.</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {categories.map((cat) => {
          const realCount = items.filter(cat.matches).length;
          const count = realCount + cat.baseline;
          return <RoleTile key={cat.key} label={cat.label} count={count} tone={cat.tone} testId={`jobs-tile-${cat.key}`} />;
        })}
      </div>

      <div className="mt-6 text-center">
        <Link
          to="/register"
          data-testid="jobs-teaser-cta"
          className="inline-flex items-center gap-2 btn btn-primary text-base px-5 py-2.5"
        >
          Join PJP to view + apply <ArrowRight className="h-4 w-4" />
        </Link>
        <div className="mono text-[10px] text-white/40 mt-2">Free to browse crew · Paid membership to apply.</div>
      </div>
    </section>
  );
}

function RoleTile({ label, count, tone = "cyan", testId }) {
  const toneMap = {
    cyan:   { border: "border-cyan/30",   text: "text-cyan",   bg: "bg-cyan/[0.04]"   },
    purple: { border: "border-purple/30", text: "text-purple", bg: "bg-purple/[0.05]" },
    orange: { border: "border-orange-hud/30", text: "text-orange-hud", bg: "bg-orange-hud/[0.05]" },
    avail:  { border: "border-avail/30",  text: "text-avail",  bg: "bg-avail/[0.05]"  },
    gold:   { border: "border-champagne/30", text: "text-champagne", bg: "bg-champagne/[0.05]" },
  };
  const t = toneMap[tone] || toneMap.cyan;
  return (
    <Link
      to="/register"
      data-testid={testId}
      className={`relative block rounded-lg border ${t.border} ${t.bg} p-4 hover:scale-[1.02] transition-transform`}
    >
      <div className="absolute top-2 right-2 flex items-center gap-1 mono text-[8px] tracking-widest text-white/40">
        <Lock className="h-2.5 w-2.5" /> GATED
      </div>
      <div className={`font-display font-semibold text-3xl leading-none ${t.text} mb-1`}>{count}</div>
      <div className="text-[13px] text-white/85 font-medium leading-tight">{label}</div>
      <div className="mono text-[10px] text-white/45 mt-1">open opportunities</div>
    </Link>
  );
}
